import { COIN_SYMBOL_ID } from "../../SymbolData";

/**
 * coinValues — the number every coin on the base and free-spins reels shows.
 *
 * GDD ("Fireball Coin Symbols"): coins appear in the base game, free spins and
 * Hold & Win, and every one shows its value — except the jackpot coin, whose
 * tier word is its face. The server sends values ONLY for the coins that
 * trigger Hold & Win (contract §8.2, §14.3 — "non-trigger coins need a
 * client-side display value"), so:
 *
 *   a trigger coin   its value from storage.new_cells, points -> money
 *   any other coin   a cosmetic value: one of the GDD's coin multipliers
 *                    (0.5x … 5x — the H&W spinning coins' list) × the bet
 *
 * Decided ONCE per round, in the adapter (round.coinValues, "reel_row"), so
 * the spinning picture that lands and the spine that plays on it show the SAME
 * number. Values are in the same unit the Hold & Win cells show.
 */

// The GDD's coin multipliers (paytable, "Coin Symbols") — the contract's
// common coin values, options.coin_values 10..100 points.
export const COIN_MULTIPLIERS = [0.5, 1, 1.5, 2, 3, 4, 5];

const CONTRACT_BASE_BET = 20;

/** The stake the values are shown against — the free-rounds bet when active. */
export const coinValueBet = () => {
  // Required lazily: the PopiPlay adapter imports this module, and a static
  // store import would close the store -> slices -> adapter cycle the adapter
  // itself avoids (it imports the store dynamically for the same reason).
  // eslint-disable-next-line global-require
  const state = require("../../State/Store/store").default.getState();
  const freeRoundsActive = state?.freeRounds?.status === "active";
  const freeRoundsBet = state?.freeRounds?.betValue || null;
  return (freeRoundsActive && freeRoundsBet) || state?.game?.bet || 1;
};

// The seven cosmetic values for the last bet asked for — rebuilt only when the
// bet changes, so a coin wrapping on the strip does no maths.
let cosmeticCache = { bet: null, values: [] };

/** The GDD coin values against `bet` (every value a passing coin can show). */
export const cosmeticCoinValues = (bet = coinValueBet()) => {
  if (cosmeticCache.bet !== bet) {
    cosmeticCache = { bet, values: COIN_MULTIPLIERS.map((m) => Math.round(m * bet)) };
  }
  return cosmeticCache.values;
};

/** A random GDD coin value against `bet` (a coin passing on the strip). */
export const cosmeticCoinValue = (bet = coinValueBet()) => {
  const values = cosmeticCoinValues(bet);
  return values[Math.floor(Math.random() * values.length)];
};

/**
 * Give every plain coin on `round.reels` its value, as `round.coinValues`.
 *
 * @param {object} round        the adapted round ({ reels: [reel][row] })
 * @param {Array}  [newCells]   storage.new_cells of a TRIGGER step —
 *                              [[reel, row, points, kind], …] — or null
 * @param {number} [bet]        the round's bet (outcome.bet)
 * @param {number} [baseBet]    options.base_bet (20)
 */
export const assignCoinValues = (round, newCells = null, bet = null, baseBet = null) => {
  if (!round?.reels) return;
  const stake = bet > 0 ? bet : coinValueBet();
  const factor = stake / (baseBet > 0 ? baseBet : CONTRACT_BASE_BET);
  const server = new Map();
  (Array.isArray(newCells) ? newCells : []).forEach(([reel, row, points, kind]) => {
    if (kind === "coin" && points > 0) server.set(`${reel}_${row}`, Math.round(points * factor));
  });
  const values = {};
  round.reels.forEach((column, reel) => {
    (column || []).forEach((id, row) => {
      if (id !== COIN_SYMBOL_ID) return; // plain coins only — never the jackpot coin
      const key = `${reel}_${row}`;
      values[key] = server.get(key) ?? cosmeticCoinValue(stake);
    });
  });
  round.coinValues = values;
};

/** The value the coin at (reel, row) of `round` shows, or null. */
export const coinValueAt = (round, reel, row) =>
  round?.coinValues?.[`${reel}_${row}`] ?? null;
